import { useEffect, useId, useState } from 'react'
import type { JSX } from 'react'

import {
  Badge,
  BookmarkIcon,
  Button,
  Card,
  Collapsible,
  CounterInput,
  CreditCardIcon,
  Dialog,
  Flex,
  FolderIcon,
  GlobeIcon,
  IconButton,
  Input,
  Notice,
  PlusIcon,
  Popover,
  Select,
  ShuffleIcon,
  Text,
  ToggleGroup,
  TrashIcon,
  UsersIcon,
  VisuallyHidden,
  WandSparklesIcon,
  XIcon,
} from '@buffer-mono/popcorn'

import {
  DEFAULT_POSTS_PER_CHANNEL,
  DEFAULT_SCENARIO,
  describeScenario,
  distributePostsRandomly,
  EMPTY_POST_COUNTS,
  isChannelsOverride,
  isMembersOverride,
  isPlanOverride,
  isScenarioActive,
  isService,
  PLAN_TIER_OPTIONS,
  POST_TAB_OPTIONS,
  SERVICE_OPTIONS,
} from './scenario'
import type {
  MocktailChannelSpec,
  MocktailScenario,
  PostTabKey,
} from './scenario'
import type { MocktailPreset } from './store/scenarioPresetsStore'
import {
  deletePreset,
  readPresets,
  savePreset,
} from './store/scenarioPresetsStore'
import { useMocktailScenario } from './useMocktailScenario'

import styles from './MocktailConfigDialog.module.css'

function AxisSection({
  title,
  description,
  icon,
  control,
  extra,
}: {
  title: string
  description: string
  icon: JSX.Element
  control: JSX.Element
  extra?: JSX.Element
}): JSX.Element {
  const headingId = useId()

  return (
    <section aria-labelledby={headingId} className={styles.axis}>
      <Flex justify="between" align="baseline" gap="sm" wrap="wrap">
        <Flex align="baseline" gap="xs" className={styles.axisLabel}>
          <span className={styles.axisIcon}>{icon}</span>
          <Flex direction="column" gap="3xs">
            <Text weight="medium" id={headingId}>
              {title}
            </Text>
            <Text size="sm" color="subtle">
              {description}
            </Text>
          </Flex>
        </Flex>
        {control}
      </Flex>
      {extra}
    </section>
  )
}

function ProfilesRow({
  draft,
  onLoad,
}: {
  draft: MocktailScenario
  onLoad: (scenario: MocktailScenario) => void
}): JSX.Element {
  const [presets, setPresets] = useState<MocktailPreset[]>(() => readPresets())
  const [selectedId, setSelectedId] = useState('')
  const [isSaveOpen, setIsSaveOpen] = useState(false)
  const [presetName, setPresetName] = useState('')

  const handleSaveOpenChange = (nextOpen: boolean): void => {
    setIsSaveOpen(nextOpen)
    if (!nextOpen) setPresetName('')
  }

  const handleSave = (): void => {
    const trimmed = presetName.trim()
    if (!trimmed) return
    const saved = savePreset(trimmed, draft)
    setPresets(readPresets())
    setSelectedId(saved.id)
    handleSaveOpenChange(false)
  }

  const handleLoad = (id: string): void => {
    setSelectedId(id)
    const preset = presets.find((candidate) => candidate.id === id)
    if (preset) onLoad(preset.scenario)
  }

  const handleDelete = (): void => {
    if (!selectedId) return
    deletePreset(selectedId)
    setPresets(readPresets())
    setSelectedId('')
  }

  return (
    <Flex direction="column" gap="xs" className={styles.profilesRow}>
      <Flex align="center" gap="2xs" className={styles.profilesLabel}>
        <FolderIcon size="small" />
        <Text size="sm" weight="medium">
          Profiles
        </Text>
      </Flex>
      <Flex align="center" gap="xs">
        <Select
          value={selectedId}
          onValueChange={handleLoad}
          disabled={presets.length === 0}
        >
          <Select.Trigger
            placeholder={
              presets.length === 0
                ? 'No saved profiles yet'
                : 'Load a saved profile'
            }
            className={styles.profilesSelectTrigger}
          />
          <Select.Content>
            {presets.map((preset) => (
              <Select.Item key={preset.id} value={preset.id}>
                {preset.name}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        <IconButton
          label="Delete selected profile"
          variant="tertiary"
          size="small"
          onClick={handleDelete}
          disabled={!selectedId}
        >
          <TrashIcon size="small" />
        </IconButton>
        <Popover open={isSaveOpen} onOpenChange={handleSaveOpenChange}>
          <Popover.Trigger>
            <Button variant="secondary" size="small">
              <BookmarkIcon size="small" />
              Save profile
            </Button>
          </Popover.Trigger>
          <Popover.Content align="end" size="medium">
            <Flex direction="column" gap="sm">
              <Text weight="medium" size="sm">
                Save current configuration
              </Text>
              <Input
                aria-label="Profile name"
                placeholder="e.g. Maxed-out team"
                size="small"
                value={presetName}
                onChange={(event) => setPresetName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') handleSave()
                }}
              />
              <Flex justify="end">
                <Button
                  variant="primary"
                  size="small"
                  onClick={handleSave}
                  disabled={!presetName.trim()}
                >
                  Save
                </Button>
              </Flex>
            </Flex>
          </Popover.Content>
        </Popover>
      </Flex>
    </Flex>
  )
}

type ChannelProfileField = 'name' | 'handle' | 'avatarUrl'

function ChannelRow({
  spec,
  index,
  removeDisabled,
  onServiceChange,
  onRemove,
  onPostCountChange,
  onProfileFieldChange,
}: {
  spec: MocktailChannelSpec
  index: number
  removeDisabled: boolean
  onServiceChange: (value: string) => void
  onRemove: () => void
  onPostCountChange: (tab: PostTabKey, value: number) => void
  onProfileFieldChange: (field: ChannelProfileField, value: string) => void
}): JSX.Element {
  return (
    <Collapsible className={styles.channelRow}>
      <Flex align="center" gap="xs">
        <Select value={spec.service} onValueChange={onServiceChange}>
          <Select.Trigger className={styles.channelSelectTrigger} />
          <Select.Content>
            {SERVICE_OPTIONS.map((option) => (
              <Select.Item key={option.value} value={option.value}>
                {option.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select>
        <Collapsible.Trigger className={styles.channelExpandTrigger}>
          <VisuallyHidden>
            Configure post counts for channel {index + 1}
          </VisuallyHidden>
        </Collapsible.Trigger>
        <IconButton
          label={`Remove channel ${index + 1}`}
          variant="tertiary"
          size="small"
          onClick={onRemove}
          disabled={removeDisabled}
        >
          <XIcon size="small" />
        </IconButton>
      </Flex>
      <Collapsible.Content>
        <Flex direction="column" gap="xs" className={styles.channelDetails}>
          <Flex direction="column" gap="2xs">
            <Text size="sm" color="subtle">
              Profile (optional)
            </Text>
            <Flex gap="xs" wrap="wrap">
              <Input
                aria-label={`Name for channel ${index + 1}`}
                placeholder={`Mock ${spec.service} Channel ${index + 1}`}
                size="small"
                value={spec.name ?? ''}
                onChange={(event) =>
                  onProfileFieldChange('name', event.target.value)
                }
              />
              <Input
                aria-label={`Handle for channel ${index + 1}`}
                placeholder="@handle"
                size="small"
                value={spec.handle ?? ''}
                onChange={(event) =>
                  onProfileFieldChange('handle', event.target.value)
                }
              />
              <Input
                aria-label={`Avatar URL for channel ${index + 1}`}
                placeholder="https://…"
                size="small"
                value={spec.avatarUrl ?? ''}
                onChange={(event) =>
                  onProfileFieldChange('avatarUrl', event.target.value)
                }
              />
            </Flex>
          </Flex>
          <Flex gap="xs" wrap="wrap" className={styles.channelPostCounts}>
            {POST_TAB_OPTIONS.map((option) => (
              <CounterInput
                key={option.value}
                aria-label={`${option.label} posts for channel ${index + 1}`}
                className={styles.counter}
                value={spec.postCounts[option.value]}
                onChange={(value) => onPostCountChange(option.value, value)}
                min={0}
                max={500}
                suffix={option.label}
              />
            ))}
          </Flex>
        </Flex>
      </Collapsible.Content>
    </Collapsible>
  )
}

export function MocktailConfigDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}): JSX.Element {
  const { scenario, setScenario } = useMocktailScenario()

  // A local draft, separate from the persisted/active scenario — every control below edits this,
  // and none of it takes effect (persisted, worker started, page reloaded) until Apply. Re-synced
  // from the real scenario on the closed→open transition so a reopened dialog starts from ground
  // truth rather than a stale edit abandoned by a previous Escape/backdrop dismissal.
  const [draft, setDraft] = useState<MocktailScenario>(scenario)
  useEffect(() => {
    if (open) setDraft(scenario)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-syncing on the open transition, not on every scenario change
  }, [open])

  const draftActive = isScenarioActive(draft)
  const draftLabels = describeScenario(draft)

  // Local, not persisted: only the per-channel queue count it produces via "distribute randomly"
  // gets saved to the draft. Keeping the total itself ephemeral avoids it drifting out of sync
  // with the sum of individual counts if channels are added/removed/hand-edited afterward.
  const [totalPostsInput, setTotalPostsInput] = useState(20)

  const handleApply = (): void => {
    const changed = JSON.stringify(scenario) !== JSON.stringify(draft)
    setScenario(() => draft)
    if (changed) {
      // A changed scenario needs a full reload — Apollo's cache and any already-rendered app
      // state were built from the pre-change (or real) responses and won't otherwise pick up the
      // new patches. Skipping onOpenChange here is intentional: the page is about to reload, so
      // there's no need to also flip the dialog's own open state first.
      window.location.reload()
      return
    }
    onOpenChange(false)
  }

  const handleResetDraft = (): void => {
    setDraft(DEFAULT_SCENARIO)
  }

  const handleChannelsChange = (value: string): void => {
    if (!isChannelsOverride(value)) return
    setDraft((prev) => ({
      ...prev,
      channels: value,
      channelSpecs:
        value === 'many' ? prev.channelSpecs : DEFAULT_SCENARIO.channelSpecs,
    }))
  }

  const handleChannelServiceChange = (id: string, value: string): void => {
    if (!isService(value)) return
    setDraft((prev) => ({
      ...prev,
      channelSpecs: prev.channelSpecs.map((spec) =>
        spec.id === id ? { ...spec, service: value } : spec,
      ),
    }))
  }

  const handleAddChannel = (): void => {
    setDraft((prev) => ({
      ...prev,
      channelSpecs: [
        ...prev.channelSpecs,
        {
          id: crypto.randomUUID(),
          service: 'instagram',
          postCounts: {
            ...EMPTY_POST_COUNTS,
            queue: DEFAULT_POSTS_PER_CHANNEL,
          },
        },
      ],
    }))
  }

  const handleRemoveChannel = (id: string): void => {
    setDraft((prev) => ({
      ...prev,
      channelSpecs: prev.channelSpecs.filter((spec) => spec.id !== id),
    }))
  }

  const handleDistributePosts = (): void => {
    setDraft((prev) => {
      const shares = distributePostsRandomly(
        totalPostsInput,
        prev.channelSpecs.length,
      )
      return {
        ...prev,
        channelSpecs: prev.channelSpecs.map((spec, index) => ({
          ...spec,
          postCounts: { ...spec.postCounts, queue: shares[index] ?? 0 },
        })),
      }
    })
  }

  const handlePostCountChange = (
    id: string,
    tab: PostTabKey,
    value: number,
  ): void => {
    setDraft((prev) => ({
      ...prev,
      channelSpecs: prev.channelSpecs.map((spec) =>
        spec.id === id
          ? { ...spec, postCounts: { ...spec.postCounts, [tab]: value } }
          : spec,
      ),
    }))
  }

  const handleProfileFieldChange = (
    id: string,
    field: ChannelProfileField,
    value: string,
  ): void => {
    setDraft((prev) => ({
      ...prev,
      channelSpecs: prev.channelSpecs.map((spec) =>
        spec.id === id ? { ...spec, [field]: value } : spec,
      ),
    }))
  }

  const handleMembersChange = (value: string): void => {
    if (!isMembersOverride(value)) return
    setDraft((prev) => ({
      ...prev,
      members: value,
      membersCount:
        value === 'many' ? prev.membersCount : DEFAULT_SCENARIO.membersCount,
    }))
  }

  const handlePlanChange = (value: string): void => {
    if (!isPlanOverride(value)) return
    setDraft((prev) => ({ ...prev, plan: value }))
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="large" className={styles.content}>
        <Dialog.Header>
          <Dialog.Title>
            <Flex align="center" gap="xs">
              <WandSparklesIcon size="small" className={styles.titleIcon} />
              Mocktail
              <Badge size="small" variant={draftActive ? 'success' : 'neutral'}>
                {draftActive ? 'Mocks active' : 'Real data'}
              </Badge>
            </Flex>
          </Dialog.Title>
          <VisuallyHidden>
            <Dialog.Description>
              Reshape this organization&apos;s channels, members, and plan tier
              to test different scenarios.
            </Dialog.Description>
          </VisuallyHidden>
          <Dialog.CloseButton />
        </Dialog.Header>
        <Dialog.Body className={styles.body}>
          <ProfilesRow draft={draft} onLoad={setDraft} />

          {draftActive && (
            <Notice variant="warning" className={styles.activeNotice}>
              <Notice.Heading>Activates when you click Apply</Notice.Heading>
              <Notice.Text>{draftLabels.join(' · ')}</Notice.Text>
            </Notice>
          )}

          <Card padding="none" className={styles.settingsPanel}>
            <AxisSection
              title="Channels"
              description="Simulate a brand-new or maxed-out account"
              icon={<GlobeIcon size="small" />}
              control={
                <ToggleGroup
                  value={draft.channels}
                  onChange={handleChannelsChange}
                >
                  <ToggleGroup.Item value="off">Real</ToggleGroup.Item>
                  <ToggleGroup.Item value="empty">None</ToggleGroup.Item>
                  <ToggleGroup.Item value="many">Many</ToggleGroup.Item>
                </ToggleGroup>
              }
              extra={
                draft.channels === 'many' ? (
                  <Flex
                    direction="column"
                    gap="xs"
                    className={styles.channelList}
                  >
                    <Flex align="center" gap="xs">
                      <CounterInput
                        aria-label="Total queue posts"
                        className={styles.counter}
                        value={totalPostsInput}
                        onChange={setTotalPostsInput}
                        min={0}
                        max={1000}
                        suffix="posts"
                      />
                      <IconButton
                        label="Distribute queue posts randomly across channels"
                        variant="tertiary"
                        size="small"
                        onClick={handleDistributePosts}
                      >
                        <ShuffleIcon size="small" />
                      </IconButton>
                    </Flex>
                    {draft.channelSpecs.map((spec, index) => (
                      <ChannelRow
                        key={spec.id}
                        spec={spec}
                        index={index}
                        removeDisabled={draft.channelSpecs.length <= 1}
                        onServiceChange={(value) =>
                          handleChannelServiceChange(spec.id, value)
                        }
                        onRemove={() => handleRemoveChannel(spec.id)}
                        onPostCountChange={(tab, value) =>
                          handlePostCountChange(spec.id, tab, value)
                        }
                        onProfileFieldChange={(field, value) =>
                          handleProfileFieldChange(spec.id, field, value)
                        }
                      />
                    ))}
                    <Button
                      variant="secondary"
                      size="small"
                      onClick={handleAddChannel}
                    >
                      <PlusIcon size="small" />
                      Add channel
                    </Button>
                  </Flex>
                ) : undefined
              }
            />

            <AxisSection
              title="Members"
              description="Simulate a team with lots of teammates"
              icon={<UsersIcon size="small" />}
              control={
                <ToggleGroup
                  value={draft.members}
                  onChange={handleMembersChange}
                >
                  <ToggleGroup.Item value="off">Real</ToggleGroup.Item>
                  <ToggleGroup.Item value="many">Many</ToggleGroup.Item>
                </ToggleGroup>
              }
              extra={
                draft.members === 'many' ? (
                  <CounterInput
                    aria-label="Number of members"
                    className={styles.counter}
                    value={draft.membersCount}
                    onChange={(value) =>
                      setDraft((prev) => ({ ...prev, membersCount: value }))
                    }
                    min={1}
                    max={50}
                    suffix="members"
                  />
                ) : undefined
              }
            />

            <AxisSection
              title="Plan tier"
              description="Overrides the plan shown in the UI — display only for now"
              icon={<CreditCardIcon size="small" />}
              control={
                <Select value={draft.plan} onValueChange={handlePlanChange}>
                  <Select.Trigger placeholder="Real plan" />
                  <Select.Content>
                    <Select.Item value="off">Real plan</Select.Item>
                    {PLAN_TIER_OPTIONS.map((option) => (
                      <Select.Item key={option.value} value={option.value}>
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              }
            />
          </Card>
        </Dialog.Body>
        <Dialog.Footer>
          <Button
            variant="secondary"
            onClick={handleResetDraft}
            disabled={!draftActive}
          >
            Reset all
          </Button>
          <Button variant="primary" onClick={handleApply}>
            Apply
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
